// Publicar conteúdo: o que mudou desde a última versão, notas da versão e histórico de versões publicadas.
import { useApi } from "@ecliptica/api-client/react";
import { LANGS, useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Label, PageHeader, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Textarea, toast } from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";
import { formatDate } from "../../lib/format";

type Pending = { published: { version: number; createdAt: string } | null; changes: { id: string; fields: string[]; texts: Record<string, string[]>; added?: boolean }[] };
// Ficheiros do jogo de cada versão (o jogo leva os textos de TODAS as línguas mantidas, ativas ou não).
const FILES = ["cards.json", ...LANGS.map((l) => `locales/${l}.json`)];

export function PublishPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useQuery({ queryKey: ["content-pending"], queryFn: async () => (await (await api.v1.admin.content.pending.$get()).json()) as unknown as Pending });
  const versions = useQuery({
    queryKey: ["content-versions"],
    queryFn: async () => (await (await api.v1.admin.content.versions.$get()).json()) as unknown as { version: number; hash: string; notes: string | null; createdAt: string; engineMin: number }[],
  });

  async function publish() {
    setBusy(true);
    const res = await api.v1.admin.content.publish.$post({ json: { notes: notes.trim() } });
    setBusy(false);
    if (!res.ok) return toast.error(t("error.generic"));
    const body = (await res.json()) as { version: number };
    toast.success(t("content.pub.done", { version: body.version }));
    setNotes("");
    for (const k of ["content-pending", "content-versions", "content-cards", "content-card"]) await qc.invalidateQueries({ queryKey: [k] });
  }

  const p = pending.data;
  return (
    <>
      <Link to="/cards" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {t("nav.cards")}
      </Link>
      <PageHeader title={t("content.pub.title")} description={t("content.pub.description")} />
      <div className="flex max-w-4xl flex-col gap-4">
        {!p ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <Alert>{p.published ? t("content.pub.lastVersion", { version: p.published.version, date: formatDate(p.published.createdAt, i18n.language) }) : t("content.pub.never")}</Alert>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {t("content.pub.pending")} ({p.changes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {p.changes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("content.pub.none")}</p>
                ) : (
                  <ul className="flex flex-col gap-1.5 text-sm">
                    {p.changes.map((c) => (
                      <li key={c.id}>
                        <Link to={`/cards/${c.id}`} className="font-mono font-semibold text-gold-bright hover:underline">
                          {c.id}
                        </Link>{" "}
                        <span className="text-muted-foreground">
                          {c.added
                            ? t("content.pub.added")
                            : [
                                c.fields.length ? t("content.pub.fields", { list: c.fields.join(", ") }) : null,
                                ...Object.entries(c.texts).map(([l, f]) => t("content.pub.texts", { lang: l.toUpperCase(), list: f.join(", ") })),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
            {can("content.publish") && p.changes.length > 0 && (
              <Card>
                <CardContent className="flex flex-col gap-3 p-4">
                  <Label htmlFor="notes">{t("content.pub.notes")}</Label>
                  <Textarea id="notes" maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <Button variant="primary" className="self-start" disabled={busy || notes.trim().length < 3} onClick={() => void publish()}>
                    {t("content.pub.publish")}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("content.pub.versions")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>{t("audit.when")}</TableHead>
                  <TableHead>{t("audit.details")}</TableHead>
                  <TableHead>{t("content.pub.files")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(versions.data ?? []).map((v) => (
                  <TableRow key={v.version}>
                    <TableCell className="font-mono font-semibold">{v.version}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(v.createdAt, i18n.language)}</TableCell>
                    <TableCell className="text-sm">
                      {v.notes}
                      <span className="block font-mono text-[10px] text-muted-foreground">{v.hash.slice(0, 16)}</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {FILES.map((f) => (
                        <a key={f} href={`/v1/public/content/${v.version}/${f}`} target="_blank" rel="noreferrer" className="block text-gold-bright hover:underline">
                          {f}
                        </a>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="mt-3 text-xs text-muted-foreground">{t("content.pub.exportHint")}</p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
